<template>
  <Dialog
    :visible="visible"
    modal
    :header="isEdit ? 'Edit Transcript Template' : 'New Transcript Template'"
    class="w-full max-w-lg"
    :draggable="false"
    @update:visible="emit('update:visible', $event)"
  >
    <form class="space-y-5 pt-2" data-testid="transcript-template-form" @submit.prevent="submit">
      <!-- Name -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Name <span class="text-red-500">*</span></label>
        <InputText
          v-model="form.name"
          class="w-full"
          placeholder="e.g. Official Transcript"
          data-testid="transcript-template-name"
        />
      </div>

      <!-- Design Style -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Design Style <span class="text-red-500">*</span></label>
        <Select
          v-model="form.style_code"
          :options="designOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select a style"
          class="w-full"
          data-testid="transcript-template-style"
        />
      </div>

      <!-- Column Visibility -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Column Visibility</label>
        <p class="text-xs text-gray-400 mb-3">Toggle which columns appear on the transcript and customize their labels.</p>
        <div class="space-y-2">
          <div
            v-for="col in columnDefs"
            :key="col.key"
            class="flex items-center gap-3"
          >
            <Checkbox
              v-model="form.properties.columns[col.key].visible"
              binary
              :inputId="`col-${col.key}`"
              :data-testid="`transcript-template-col-${col.key}`"
            />
            <InputText
              v-model="form.properties.columns[col.key].label"
              class="flex-1"
              size="small"
              :data-testid="`transcript-template-col-label-${col.key}`"
            />
          </div>
        </div>
      </div>

      <!-- Header Settings -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Header Settings</label>
        <div class="space-y-3">
          <div>
            <label class="block text-xs text-gray-500 mb-1">Title</label>
            <InputText
              v-model="form.properties.header.title"
              class="w-full"
              data-testid="transcript-template-header-title"
            />
          </div>
          <div>
            <label class="block text-xs text-gray-500 mb-1">Subtitle</label>
            <InputText
              v-model="form.properties.header.subtitle"
              class="w-full"
              data-testid="transcript-template-header-subtitle"
            />
          </div>
          <div class="flex items-center gap-3">
            <Checkbox
              v-model="form.properties.header.show_logo"
              binary
              inputId="header-show-logo"
              data-testid="transcript-template-show-logo"
            />
            <label for="header-show-logo" class="text-sm text-gray-700">Show School Logo</label>
          </div>
          <div>
            <label class="block text-xs text-gray-500 mb-1">Logo Position</label>
            <Select
              v-model="form.properties.header.logo_position"
              :options="logoPositionOptions"
              optionLabel="label"
              optionValue="value"
              class="w-full"
              data-testid="transcript-template-logo-position"
            />
          </div>
        </div>
      </div>

      <!-- Options -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Options</label>
        <div class="space-y-2">
          <div class="flex items-center gap-3">
            <Checkbox
              v-model="form.properties.options.show_partial_footnote"
              binary
              inputId="opt-partial-footnote"
              data-testid="transcript-template-partial-footnote"
            />
            <label for="opt-partial-footnote" class="text-sm text-gray-700">Show Partial Footnote</label>
            <i
              class="pi pi-info-circle text-gray-400 text-xs"
              v-tooltip.top="'Display a note when some semester results are pending publication'"
            ></i>
          </div>
          <div class="flex items-center gap-3">
            <Checkbox
              v-model="form.properties.options.show_cover_letter"
              binary
              inputId="opt-cover-letter"
              data-testid="transcript-template-cover-letter"
            />
            <label for="opt-cover-letter" class="text-sm text-gray-700">Show Cover Letter</label>
          </div>
          <div class="flex items-center gap-3">
            <Checkbox
              v-model="form.properties.options.show_student_info"
              binary
              inputId="opt-student-info"
              data-testid="transcript-template-student-info"
            />
            <label for="opt-student-info" class="text-sm text-gray-700">Show Student Info</label>
          </div>
        </div>
      </div>
    </form>

    <template #footer>
      <Button
        size="small"
        label="Cancel"
        severity="secondary"
        text
        data-testid="transcript-template-cancel"
        @click="close"
      />
      <Button
        size="small"
        :label="isEdit ? 'Save' : 'Create'"
        :loading="loading"
        :disabled="loading"
        data-testid="transcript-template-submit"
        @click="submit"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import type { TranscriptTemplate } from '@/types/composition-functions';

interface ColumnConfig {
  visible: boolean;
  label: string;
}

const columnDefs = [
  { key: 'code', defaultLabel: 'Course Code' },
  { key: 'title', defaultLabel: 'Course Title' },
  { key: 'units', defaultLabel: 'Units' },
  { key: 'score', defaultLabel: 'Score' },
  { key: 'grade', defaultLabel: 'Grade' },
  { key: 'grade_point', defaultLabel: 'Grade Point' },
  { key: 'weighted_grade_point', defaultLabel: 'Weighted Grade Point' },
] as const;

type ColumnKey = typeof columnDefs[number]['key'];

interface TemplateProperties {
  columns: Record<ColumnKey, ColumnConfig>;
  header: {
    title: string;
    subtitle: string;
    show_logo: boolean;
    logo_position: string;
  };
  options: {
    show_partial_footnote: boolean;
    show_cover_letter: boolean;
    show_student_info: boolean;
  };
}

const props = defineProps<{
  visible: boolean;
  template?: TranscriptTemplate | null;
  designOptions: Array<{ value: string; label: string }>;
  loading?: boolean;
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
  save: [data: { name: string; style_code: string; properties: Record<string, unknown> }];
}>();

const isEdit = computed(() => !!props.template?.id);

const logoPositionOptions = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const defaultProperties = (): TemplateProperties => ({
  columns: Object.fromEntries(
    columnDefs.map((col) => [col.key, { visible: true, label: col.defaultLabel }]),
  ) as Record<ColumnKey, ColumnConfig>,
  header: {
    title: 'ACADEMIC TRANSCRIPT',
    subtitle: 'Office of the Registrar',
    show_logo: true,
    logo_position: 'left',
  },
  options: {
    show_partial_footnote: true,
    show_cover_letter: false,
    show_student_info: true,
  },
});

const initialForm = () => ({
  name: '',
  style_code: 'classic',
  properties: defaultProperties(),
});

const form = reactive(initialForm());

watch(
  () => props.visible,
  (open) => {
    if (open) {
      if (props.template) {
        form.name = props.template.name;
        form.style_code = props.template.style_code;

        // Merge saved properties with defaults to handle any missing keys
        const saved = (props.template.properties ?? {}) as Partial<TemplateProperties>;
        const defaults = defaultProperties();

        form.properties.columns = { ...defaults.columns };
        if (saved.columns) {
          for (const { key } of columnDefs) {
            const savedColumn = saved.columns[key];
            if (savedColumn) {
              form.properties.columns[key] = { ...form.properties.columns[key], ...savedColumn };
            }
          }
        }

        form.properties.header = { ...defaults.header, ...(saved.header ?? {}) };
        form.properties.options = { ...defaults.options, ...(saved.options ?? {}) };
      } else {
        Object.assign(form, initialForm());
      }
    }
  },
);

const close = () => emit('update:visible', false);

const submit = () => {
  if (!form.name.trim()) return;

  emit('save', {
    name: form.name,
    style_code: form.style_code,
    properties: JSON.parse(JSON.stringify(form.properties)),
  });
};
</script>
